import { ChevronLeft, ChevronRight, Phone, Star } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BookingDoc } from '../lib/types';

export type MaintBlock = { start: Date; end: Date; label: string; tone?: 'red' | 'grey' };

type CalView = 'month' | 'week' | 'day';
// The pilot's preferred default view persists across visits (per browser).
const DEFAULT_VIEW_KEY = 'solari-calendar-default-view';
// Month answers "what happened this month". Nobody opens a schedule for that —
// they open it to find out when they can fly, and a month cell can only fit
// "9a C…" before it truncates. Week answers the real question in one look.
// Anyone who disagrees can star Month and keep it.
const FALLBACK_VIEW: CalView = 'week';

function readDefaultView(): CalView {
  if (typeof window === 'undefined') return FALLBACK_VIEW;
  try {
    const v = window.localStorage.getItem(DEFAULT_VIEW_KEY);
    if (v === 'month' || v === 'week' || v === 'day') return v;
  } catch {
    /* ignore */
  }
  return FALLBACK_VIEW;
}

// Compact time for narrow cells: 9:00 AM → "9a", 2:30 PM → "2:30p".
function fmtShort(d: Date) {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h < 12 ? 'a' : 'p';
  h = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h}${ap}` : `${h}:${m < 10 ? '0' : ''}${m}${ap}`;
}
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const sameDay = (a: Date, b: Date) => dayKey(a) === dayKey(b);
function startOfWeek(d: Date) {
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  s.setDate(s.getDate() - s.getDay());
  return s;
}
function addDays(d: Date, n: number) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

interface DayCell {
  date: Date;
  inMonth: boolean;
  bookings: BookingDoc[];
  maint: MaintBlock[];
}

interface Props {
  bookings: BookingDoc[];
  myUid?: string;
  onPickDay?: (d: Date) => void;
  initialMonth?: Date;
  maintBlocks?: MaintBlock[];
  /** When set, booking blocks/chips become clickable (admin edit-from-calendar). */
  onPickBooking?: (b: BookingDoc) => void;
}

export function MonthCalendar({ bookings, myUid, onPickDay, initialMonth, maintBlocks = [], onPickBooking }: Props) {
  const [view, setView] = useState<CalView>(() => readDefaultView());
  const [defaultView, setDefaultView] = useState<CalView>(() => readDefaultView());
  const [anchor, setAnchor] = useState(() => initialMonth ?? new Date());
  const [picked, setPicked] = useState<{ key: string; hour: number } | null>(null);
  const today = new Date();

  // Step the calendar by month / week / day depending on the active view.
  function go(dir: number) {
    if (view === 'month') setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    else if (view === 'week') setAnchor(addDays(anchor, dir * 7));
    else setAnchor(addDays(anchor, dir));
    setPicked(null);
  }
  function jumpToDay(d: Date) {
    setAnchor(d);
    setView('day');
    setPicked(null);
  }
  function pickHour(day: Date, hour: number) {
    setPicked({ key: dayKey(day), hour });
    onPickDay?.(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0));
  }
  function saveDefault() {
    try {
      window.localStorage.setItem(DEFAULT_VIEW_KEY, view);
    } catch {
      /* ignore */
    }
    setDefaultView(view);
  }

  const title = useMemo(() => {
    if (view === 'month') return anchor.toLocaleString([], { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const ws = startOfWeek(anchor);
      const we = addDays(ws, 6);
      const left = ws.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const right = we.toLocaleDateString(
        [],
        ws.getMonth() === we.getMonth() ? { day: 'numeric' } : { month: 'short', day: 'numeric' }
      );
      return `${left} – ${right}, ${we.getFullYear()}`;
    }
    return anchor.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  }, [view, anchor]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)),
    [anchor]
  );

  return (
    <div className="card overflow-hidden">
      <div className="bg-brand text-white">
        <div className="flex items-center justify-between px-3 py-2.5 sm:px-4">
          <button
            type="button"
            onClick={() => go(-1)}
            className="rounded-md p-2 hover:bg-brand-600"
            aria-label="Previous"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex-1 text-center font-display text-sm font-semibold sm:text-base">{title}</div>
          <button
            type="button"
            onClick={() => go(1)}
            className="rounded-md p-2 hover:bg-brand-600"
            aria-label="Next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 px-3 pb-2.5 sm:px-4">
          <div className="inline-flex rounded-lg bg-brand-600 p-0.5 text-xs">
            {(['month', 'week', 'day'] as CalView[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  setView(v);
                  setPicked(null);
                }}
                className={`rounded-md px-3 py-1 capitalize transition ${
                  view === v ? 'bg-white font-semibold text-navy-900' : 'text-sky-100 hover:text-white'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={saveDefault}
            title={`Always open the calendar in ${view} view`}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-sky-100 hover:bg-brand-600 hover:text-white"
          >
            <Star size={13} className={defaultView === view ? 'fill-amber-300 text-amber-300' : ''} />
            <span className="hidden sm:inline">{defaultView === view ? 'Default view' : 'Set as default'}</span>
          </button>
        </div>
        {view !== 'month' && (
          <div className="px-3 pb-2 text-center text-[11px] text-sky-100 sm:px-4">
            Tap any open time to start a booking
          </div>
        )}
      </div>

      {view === 'month' ? (
        <MonthGrid
          anchor={anchor}
          bookings={bookings}
          maintBlocks={maintBlocks}
          myUid={myUid}
          today={today}
          onPickDayCell={jumpToDay}
          onPickBooking={onPickBooking}
        />
      ) : (
        <TimelineGrid
          days={view === 'day' ? [anchor] : weekDays}
          bookings={bookings}
          maint={maintBlocks}
          myUid={myUid}
          today={today}
          picked={picked}
          onPick={pickHour}
          onPickBooking={onPickBooking}
        />
      )}
    </div>
  );
}

function MonthGrid({
  anchor,
  bookings,
  maintBlocks,
  myUid,
  today,
  onPickDayCell,
  onPickBooking,
}: {
  anchor: Date;
  bookings: BookingDoc[];
  maintBlocks: MaintBlock[];
  myUid?: string;
  today: Date;
  onPickDayCell: (d: Date) => void;
  onPickBooking?: (b: BookingDoc) => void;
}) {
  const cells = useMemo<DayCell[]>(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - first.getDay());
    const result: DayCell[] = [];
    for (let i = 0; i < 42; i++) {
      const d = addDays(gridStart, i);
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const dayEnd = dayStart + 24 * 60 * 60 * 1000;
      result.push({
        date: d,
        inMonth: d.getMonth() === anchor.getMonth(),
        bookings: bookings.filter((b) => {
          if (b.status === 'cancelled' || b.status === 'denied') return false;
          return b.startDateTime.toDate().getTime() < dayEnd && b.endDateTime.toDate().getTime() > dayStart;
        }),
        maint: maintBlocks.filter((m) => m.start.getTime() < dayEnd && m.end.getTime() > dayStart),
      });
    }
    return result;
  }, [anchor, bookings, maintBlocks]);

  return (
    <>
      <div className="grid grid-cols-7 bg-sky-50 text-xs uppercase tracking-widest text-ink-muted">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-2 py-2 text-center font-medium">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 border-l border-t border-slate-200">
        {cells.map((c) => {
          const isToday = sameDay(c.date, today);
          return (
            <button
              key={dayKey(c.date)}
              type="button"
              onClick={() => onPickDayCell(c.date)}
              className={`relative min-h-[88px] border-b border-r border-slate-200 p-1.5 text-left transition sm:min-h-[110px] ${
                c.inMonth ? 'bg-white' : 'bg-slate-50/60'
              } hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand`}
            >
              <span
                className={`absolute left-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                  isToday ? 'bg-brand text-white' : c.inMonth ? 'text-navy-900' : 'text-slate-400'
                }`}
              >
                {c.date.getDate()}
              </span>
              <div className="mt-7 space-y-0.5">
                {c.maint.map((m, i) => (
                  <div
                    key={`m-${i}`}
                    className={`truncate rounded border px-1 py-0.5 text-[10px] font-medium leading-tight sm:text-xs ${
                      m.tone === 'red'
                        ? 'border-red-300 bg-red-100 text-red-800'
                        : 'border-slate-300 bg-slate-200 text-slate-700'
                    }`}
                  >
                    {m.tone === 'red' ? '🔴' : '🔧'} {m.label}
                  </div>
                ))}
                {c.bookings.slice(0, 3).map((b) => {
                  const mine = myUid && b.lesseeUid === myUid;
                  const tone = b.maintenanceLabel
                    ? 'bg-red-100 text-red-800 border-red-300'
                    : b.status === 'pending_approval'
                      ? 'bg-amber-100 text-amber-900 border-amber-200'
                      : mine
                      ? 'bg-brand text-white border-brand'
                      : 'bg-sky-100 text-navy-900 border-sky-200';
                  const time = fmtShort(b.startDateTime.toDate());
                  const who = b.maintenanceLabel
                    ? b.maintenanceLabel
                    : mine ? 'You' : b.lesseeName?.split(' ')[0] || 'Booked';
                  return (
                    <div
                      key={b.bookingId}
                      onClick={onPickBooking ? (ev) => { ev.stopPropagation(); onPickBooking(b); } : undefined}
                      className={`truncate rounded border px-1 py-0.5 text-[10px] leading-tight sm:text-xs ${tone} ${onPickBooking ? 'cursor-pointer hover:ring-1 hover:ring-brand-300' : ''}`}
                      title={`${time} · ${b.maintenanceLabel ? b.maintenanceLabel : mine ? 'Your booking' : b.lesseeName || 'Booked'}`}
                    >
                      <span className="font-semibold">{b.maintenanceLabel ? '🔴' : time}</span> {who}
                    </div>
                  );
                })}
                {c.bookings.length > 3 && (
                  <div className="text-[10px] text-ink-muted">+{c.bookings.length - 3} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// Apple-Calendar-style timeline: hours down the side, one column per day (1 for
// Day view, 7 for Week). Bookings are blocks at their time; tap an open slot to
// start a booking.
function TimelineGrid({
  days,
  bookings,
  maint,
  myUid,
  today,
  picked,
  onPick,
  onPickBooking,
}: {
  days: Date[];
  bookings: BookingDoc[];
  maint: MaintBlock[];
  myUid?: string;
  today: Date;
  picked: { key: string; hour: number } | null;
  onPick: (day: Date, hour: number) => void;
  onPickBooking?: (b: BookingDoc) => void;
}) {
  // Midnight-to-midnight puts nine hours of empty night between the pilot and
  // the hours anyone actually books. Show the flying day instead — but never
  // at the cost of hiding something: a booking or a maintenance block outside
  // these hours pushes the window open to include it, so nothing can fall off
  // the grid and get double-booked.
  const BASE_START = 6;
  const BASE_END = 22;
  const multi = days.length > 1;
  const { START, END } = useMemo(() => {
    let lo = BASE_START;
    let hi = BASE_END;
    const DAY_MS = 24 * 60 * 60 * 1000;
    for (const day of days) {
      const ds = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
      const de = ds + DAY_MS;
      const spans: Array<[number, number]> = [];
      for (const b of bookings) {
        if (b.status === 'cancelled' || b.status === 'denied') continue;
        const bs = b.startDateTime.toDate().getTime();
        const be = b.endDateTime.toDate().getTime();
        if (bs < de && be > ds) spans.push([bs, be]);
      }
      for (const m of maint) {
        // An all-day block paints the whole column whatever the window is, so
        // it must not be the thing that drags the window back out to midnight.
        if (m.start.getTime() <= ds && m.end.getTime() >= de) continue;
        if (m.start.getTime() < de && m.end.getTime() > ds) spans.push([m.start.getTime(), m.end.getTime()]);
      }
      for (const [bs, be] of spans) {
        if (bs <= ds) lo = 0;
        else {
          const d = new Date(bs);
          lo = Math.min(lo, Math.floor(d.getHours() + d.getMinutes() / 60));
        }
        if (be >= de) hi = 24;
        else {
          const d = new Date(be);
          hi = Math.max(hi, Math.ceil(d.getHours() + d.getMinutes() / 60));
        }
      }
    }
    return { START: Math.max(0, Math.min(lo, BASE_START)), END: Math.min(24, Math.max(hi, BASE_END)) };
  }, [days, bookings, maint]);
  const HOUR_PX = multi ? 40 : 46;
  const hours = Array.from({ length: END - START + 1 }, (_, i) => START + i);
  const fmtHour = (h: number) => {
    const hh = h % 24;
    return `${hh % 12 === 0 ? 12 : hh % 12}${hh < 12 ? 'a' : 'p'}`;
  };
  const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  // Open on the morning. Normally that is already the top of the grid; it only
  // scrolls when an early-hours booking pushed the window open.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = Math.max(0, (BASE_START - START) * HOUR_PX);
  }, [HOUR_PX, days.length, START]);

  return (
    <div ref={scrollRef} className="overflow-auto" style={{ maxHeight: 560 }}>
      {multi && (
        <div className="sticky top-0 z-20 flex border-b border-slate-200 bg-white">
          <div className="w-10 shrink-0" />
          {days.map((d) => {
            const isToday = sameDay(d, today);
            return (
              <div key={dayKey(d)} className="flex-1 py-1.5 text-center">
                <div className="text-[10px] uppercase tracking-wide text-ink-muted">
                  {d.toLocaleDateString([], { weekday: 'short' })}
                </div>
                <div
                  className={`mx-auto mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    isToday ? 'bg-brand text-white' : 'text-navy-900'
                  }`}
                >
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex" style={{ height: (END - START) * HOUR_PX + 8 }}>
        <div className="relative w-10 shrink-0">
          {/* The first hour used to be blank because it was midnight and its
              centred label would have been clipped by the top edge. Now the
              window opens at 6a, which is an hour someone books, so it is
              labelled and sits flush with the top instead of centred on it. */}
          {hours.map((h) => (
            <span
              key={h}
              className={`absolute right-1.5 text-[10px] text-ink-subtle ${
                h === START ? '' : '-translate-y-1/2'
              }`}
              style={{ top: (h - START) * HOUR_PX }}
            >
              {fmtHour(h)}
            </span>
          ))}
        </div>
        <div className="flex flex-1">
          {days.map((day) => {
            const ds = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
            const de = ds + 24 * 60 * 60 * 1000;
            const dayBk = bookings.filter(
              (b) =>
                b.status !== 'cancelled' &&
                b.status !== 'denied' &&
                b.startDateTime.toDate().getTime() < de &&
                b.endDateTime.toDate().getTime() > ds
            );
            const dayMt = maint.filter((m) => m.start.getTime() < de && m.end.getTime() > ds);
            const pickedHour = picked && picked.key === dayKey(day) ? picked.hour : null;
            return (
              <div key={dayKey(day)} className="relative flex-1 border-l border-slate-100">
                {hours.map((h) => (
                  <div
                    key={`l-${h}`}
                    className="pointer-events-none absolute inset-x-0 border-t border-slate-100"
                    style={{ top: (h - START) * HOUR_PX }}
                  />
                ))}
                {hours.slice(0, -1).map((h) => (
                  <button
                    key={`s-${h}`}
                    type="button"
                    onClick={() => onPick(day, h)}
                    className="absolute inset-x-0 transition hover:bg-sky-50 focus:bg-sky-50 focus:outline-none"
                    style={{ top: (h - START) * HOUR_PX, height: HOUR_PX }}
                    aria-label={`Book ${day.toLocaleDateString([], { weekday: 'short' })} ${fmtHour(h)}`}
                  />
                ))}
                {pickedHour != null && (
                  <div
                    className="pointer-events-none absolute inset-x-0.5 rounded-md border-2 border-dashed border-navy-500 bg-navy-50/50"
                    style={{ top: (pickedHour - START) * HOUR_PX, height: 2.5 * HOUR_PX - 2 }}
                  >
                    {!multi && (
                      <div className="px-2 py-1 text-[11px] font-semibold text-navy-700">
                        Selected — set details below
                      </div>
                    )}
                  </div>
                )}
                {dayMt.map((m, i) => {
                  const allDay = m.start.getTime() <= ds && m.end.getTime() >= de;
                  const sMs = Math.max(m.start.getTime(), ds);
                  const sh = new Date(sMs).getHours() + new Date(sMs).getMinutes() / 60;
                  const eMs = Math.min(m.end.getTime(), de);
                  const eh = new Date(eMs).getHours() + new Date(eMs).getMinutes() / 60 || END;
                  const top = allDay ? 0 : Math.max(0, (sh - START) * HOUR_PX);
                  const height = allDay
                    ? (END - START) * HOUR_PX
                    : Math.max(22, (Math.min(eh, END) - Math.max(sh, START)) * HOUR_PX - 2);
                  return (
                    <div
                      key={`maint-${i}`}
                      className={`absolute inset-x-0.5 overflow-hidden rounded-md border px-1 py-0.5 text-[10px] sm:text-xs ${
                        m.tone === 'red'
                          ? 'border-red-300 bg-red-100/95 text-red-800'
                          : 'border-slate-300 bg-slate-200/95 text-slate-700'
                      }`}
                      style={{ top, height }}
                      title={m.label}
                    >
                      <div className="truncate font-semibold">🔧 {multi ? 'Mx' : 'Maintenance'}</div>
                      {!multi && <div className="truncate opacity-80">{m.label}</div>}
                    </div>
                  );
                })}
                {dayBk.map((b) => {
                  const s = b.startDateTime.toDate();
                  const e = b.endDateTime.toDate();
                  const sh = Math.max(START, s.getHours() + s.getMinutes() / 60);
                  const eRaw = e.getHours() + e.getMinutes() / 60;
                  const eh = Math.min(END, e.getTime() >= de ? END : eRaw === 0 ? END : eRaw);
                  const top = (sh - START) * HOUR_PX;
                  const height = Math.max(20, (eh - sh) * HOUR_PX - 2);
                  const mine = b.lesseeUid === myUid;
                  const cls = b.maintenanceLabel
                    ? 'bg-red-100 border-red-300 text-red-800'
                    : b.status === 'pending_approval'
                      ? 'bg-amber-100 border-amber-300 text-amber-900'
                      : mine
                      ? 'bg-brand border-brand text-white'
                      : 'bg-sky-100 border-sky-300 text-navy-900';
                  return (
                    <div
                      key={b.bookingId}
                      onClick={onPickBooking ? (ev) => { ev.stopPropagation(); onPickBooking(b); } : undefined}
                      className={`absolute inset-x-0.5 overflow-hidden rounded-md border px-1 text-[10px] sm:text-xs ${cls} ${
                        multi ? 'py-0' : 'py-1'
                      } ${onPickBooking ? 'cursor-pointer hover:ring-2 hover:ring-brand-300' : ''}`}
                      style={{ top, height }}
                      title={`${fmtTime(s)} – ${fmtTime(e)} · ${b.maintenanceLabel ? b.maintenanceLabel : mine ? 'You' : b.lesseeName || 'Booked'}${onPickBooking ? ' — click to edit' : ''}`}
                    >
                      <div className="truncate font-semibold">
                        {b.maintenanceLabel ? `🔴 ${b.maintenanceLabel}` : multi ? fmtShort(s) : `${fmtTime(s)} – ${fmtTime(e)}`}
                      </div>
                      <div className="truncate opacity-80">
                        {b.maintenanceLabel ? `${fmtTime(s)} – ${fmtTime(e)}` : mine ? (multi ? 'You' : 'Your booking') : b.lesseeName?.split(' ')[0] || 'Booked'}
                        {!multi && !b.maintenanceLabel && b.expectedDestinations ? ` · ${b.expectedDestinations}` : ''}
                      </div>
                      {!multi && !mine && b.lesseePhone && (
                        <a
                          href={`tel:${b.lesseePhone.replace(/[^0-9+]/g, '')}`}
                          onClick={(ev) => ev.stopPropagation()}
                          className="mt-1 inline-flex items-center gap-1 font-medium text-navy-800 underline decoration-navy-300 underline-offset-2"
                        >
                          <Phone size={11} /> {b.lesseePhone}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
